import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { US_STATES } from '../shared/models/us-states';
import { ToastrService } from 'ngx-toastr';
import { InsureApiService } from '../shared/services/insure-api.service';
import {
  IdentityApiService,
  RegisterUserDto,
} from '../shared/services/identity-api.service';
import { UserRole } from '../shared/models/user-role.enum';
import { switchMap } from 'rxjs';
import { PASSWORD_PATTERN } from '../shared/utils/password-validation';
import { environment } from '../../environments/environment';

@Component({
  selector: 'ins-register-customer',
  templateUrl: './register-customer.component.html',
})
export class RegisterCustomerComponent implements OnInit {
  registerForm!: FormGroup;
  states = US_STATES;
  errorMessage = '';
  captchaIsValid = false;
  captchaResponse = '';
  captchaKey = environment.RecaptchaSiteKey;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private insureApi: InsureApiService,
    private identityApi: IdentityApiService,
  ) {}

  ngOnInit(): void {
    this.registerForm = this.fb.group({
      firstName: ['', Validators.required],
      middleName: [''],
      lastName: ['', Validators.required],
      policyNumber: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      address1: ['', Validators.required],
      address2: [''],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zipCode: ['', Validators.required],
      password: ['', [Validators.required, Validators.pattern(PASSWORD_PATTERN)]],
      confirmPassword: ['', Validators.required],
    });
  }

  get passwordMismatch(): boolean {
    return (
      this.registerForm.controls['password'].value !==
        this.registerForm.controls['confirmPassword'].value &&
      this.registerForm.controls['confirmPassword'].touched
    );
  }

  onRegister(): void {
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid || this.passwordMismatch || !this.captchaIsValid) {
      return;
    }

    this.errorMessage = '';
    const f = this.registerForm.value;
    const fullName =
      f.firstName + (f.middleName ? ' ' + f.middleName : '') + ' ' + f.lastName;

    this.insureApi
      .getPolicies(f.policyNumber, f.zipCode)
      .pipe(
        switchMap((policies) => {
          if (!policies || policies.length === 0) {
            throw { policyNotFound: true };
          }

          const user: RegisterUserDto = {
            userName: f.email,
            email: f.email,
            name: fullName,
            address: f.address1,
            address2: f.address2 || '',
            city: f.city,
            state: f.state,
            postalCode: f.zipCode,
            password: f.password,
            roles: [UserRole.Customer],
          };

          return this.identityApi.registerUser(user);
        }),
        switchMap((registeredUser) =>
          this.insureApi.insertCustomer({
            nameOfInsured: fullName,
            systemId: registeredUser.id,
          }),
        ),
      )
      .subscribe({
        next: () => {
          this.toastr.success('Registration submitted successfully.');
          this.registerForm.reset();
          this.errorMessage = '';
        },
        error: (err) => {
          if (err?.policyNotFound) {
            this.errorMessage = 'Policy number not found for this Zip Code.';
          } else if (err?.status === 409 || err?.status === 400) {
            this.errorMessage =
              'You are already registered in our system. If you have forgotten your login or password, please contact us at 1.800.444.3584. Thank you.';
          } else {
            this.errorMessage =
              'There was an error processing your registration. Please try again.';
          }
        },
      });
  }

  getCaptchaValue(captcha: string | null): void {
    this.captchaIsValid = !(captcha === null || captcha === undefined);
    this.captchaResponse = captcha || '';
  }
}
